import sortBy from 'lodash/sortBy';
import BaseModel from 'volta/models/base-model';
import { isActiveAtDate } from 'volta/models/bom-node';
import { formatDuration } from 'volta/utils/date-utils';
import { sanitized } from 'volta/utils/text-utils';

import { attr } from '@ember-data/model';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';

import type { IDateFilter } from 'volta/_pods/protected/bom/controller';
import type { IResourceName } from 'volta/models/base-model';

import type { BomNodeOrOperation, TRouteType } from 'volta/models/types/route-operation';
import type { TAduFrequency } from 'volta/models/types/planning-settings';
import type { IWorkshopIdentityInfo } from 'volta/models/types/workshop';
import type IntlService from 'ember-intl/services/intl';

export const RouteOperationResourceName: IResourceName = {
  singular: 'routeOperation',
  plural: 'routeOperations'
};

export const filterOperations = (
  ops: RouteOperation[],
  dateFilter?: IDateFilter,
  partFilter?: string
) => {
  return sortBy(
    ops.filter((op) => op.isNodeActive(dateFilter, partFilter)),
    (op) => op.operationNum
  ) as BomNodeOrOperation[];
};

export default class RouteOperation extends BaseModel {
  @service intl!: IntlService;

  @attr('string') routeId!: string;
  @attr('string') routeCode!: string;
  @attr('string') routeType!: TRouteType;
  @attr('string') manufacturedSkuId!: string;
  @attr('number') operationNum!: number;
  @attr('number') operationPriority!: number;
  @attr('number') nextOperationNum?: number;
  @attr('string') operationSkuId?: string;
  @attr('string') operationCode?: string;
  @attr('string') operationName?: string;
  @attr('string') workshopId!: string;
  @attr('string') workshopCode!: string;
  @attr('string') workshopDescription!: string;
  @attr('number') workshopCapacityHours?: number;
  @attr('string') workshopCapacityPeriod?: TAduFrequency;
  @attr('number', { defaultValue: 0 }) setupTime!: number;
  @attr('number', { defaultValue: 1 }) setupPCB!: number;
  @attr('number') processTime!: number;
  @attr('number', { defaultValue: 0 }) processPCB!: number;
  @attr('date') validFromDate?: Date;
  @attr('date') validToDate?: Date;

  get sequence() {
    return this.operationNum;
  }

  get workshop(): IWorkshopIdentityInfo {
    const { workshopId, workshopCode, workshopDescription } = this;
    return { workshopId, code: workshopCode, description: workshopDescription };
  }

  get manufacturedSku() {
    const { manufacturedSkuId, operationCode, operationName } = this;
    return { skuId: manufacturedSkuId, code: operationCode, description: operationName };
  }

  get processTimeAsString() {
    return formatDuration(this.processTime, (days) => this.intl.t('daysCount', { value: days }));
  }

  get setupTimeAsString() {
    return formatDuration(this.setupTime, (days) => this.intl.t('daysCount', { value: days }));
  }

  @cached
  get searchName() {
    return sanitized(
      `${this.operationNum}|${this.operationName}${this.operationCode ? `|${this.operationCode}` : ''}`
    );
  }

  isActiveAtDate(filter?: IDateFilter): boolean {
    return isActiveAtDate(filter, this.validFromDate, this.validToDate);
  }

  isNodeActive(dateFilter?: IDateFilter, partFilter?: string): boolean {
    return (
      this.isActiveAtDate(dateFilter) &&
      (isEmpty(partFilter) || this.searchName.includes(sanitized(partFilter)))
    );
  }

  [Type] = 'route-operation' as const;
}
